// Apps — download the Stratus softphone for phone (iOS / Android) and Windows desktop.
// URLs come from /api/mobile/app-qr: Android APK + Windows .exe are self-hosted on the
// public console (host derived from the request behind NPM); iOS is the App Store /
// TestFlight listing (env-driven, may be null before the listing is public).
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Monitor, Download, FlaskConical, ChevronRight, Cable } from 'lucide-react';
import { api, getToken } from '../lib/api';

type Platform = { url: string; qr_data_url?: string | null };
type AppInfo = {
  android?: Platform | null;
  ios?: Platform | null;
  desktop?: Platform | null;
  desktop_url?: string | null;
};

const fJson = async (url: string) => {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
};

export default function Apps() {
  const me = api.me();
  const isAdmin = me?.role === 'super_admin' || me?.role === 'reseller';
  const { data, isLoading, isError } = useQuery<AppInfo>({
    queryKey: ['app-downloads'],
    queryFn: () => fJson('/api/mobile/app-qr'),
  });

  // Fall back to building the desktop URL from the current origin if an older
  // backend doesn't return one yet.
  const desktopUrl = data?.desktop?.url || data?.desktop_url
    || `${window.location.origin}/api/desktop/app`;
  const linuxUrl = `${window.location.origin}/api/desktop/app-linux`;
  const macArmUrl = `${window.location.origin}/api/desktop/app-mac-arm64`;
  const macIntelUrl = `${window.location.origin}/api/desktop/app-mac-x64`;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Apps</h1>
        <p className="text-textSecondary text-sm">
          Download the Stratus softphone for your phone or computer.
        </p>
      </div>

      {isLoading && <div className="card p-6 text-textSecondary">Loading…</div>}
      {isError && (
        <div className="card p-6 text-textSecondary">
          Couldn't load the download links. Refresh the page to try again.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="grid gap-4 md:grid-cols-3">
          {data?.ios?.url && (
            <AppCard
              icon={<Smartphone size={22} />}
              title="iPhone & iPad"
              subtitle="iOS"
              desc="Install via TestFlight. Install the TestFlight app first, then open this link on the device."
              url={data.ios.url}
              buttonLabel="Open TestFlight"
              qr={data.ios.qr_data_url}
              external
            />
          )}

          {data?.android?.url && (
            <AppCard
              icon={<Smartphone size={22} />}
              title="Android"
              subtitle="Android phone or tablet"
              desc="Download and install the app. Android may ask you to allow installing from your browser — that is expected."
              url={data.android.url}
              buttonLabel="Download APK"
              qr={data.android.qr_data_url}
            />
          )}

          <AppCard
            icon={<Monitor size={22} />}
            title="Windows Desktop"
            subtitle="Windows 10 / 11"
            desc="Full desktop softphone — dialpad, calls, chat, voicemail, transfer, and more."
            url={desktopUrl}
            buttonLabel="Download for Windows"
          />

          <AppCard
            icon={<Monitor size={22} />}
            title="Linux Desktop"
            subtitle="Ubuntu / Debian / Fedora (AppImage)"
            desc="Same desktop softphone as Windows, as a portable AppImage. After downloading: chmod +x it and run — no install needed."
            url={linuxUrl}
            buttonLabel="Download for Linux"
          />

          <AppCard
            icon={<Monitor size={22} />}
            title="Mac (Apple Silicon)"
            subtitle="macOS — M1/M2/M3/M4"
            desc="Full desktop softphone for Apple Silicon Macs. Open the .dmg and drag Stratus to Applications. First launch: right-click the app and choose Open."
            url={macArmUrl}
            buttonLabel="Download for Mac"
          />

          <AppCard
            icon={<Monitor size={22} />}
            title="Mac (Intel)"
            subtitle="macOS — Intel"
            desc="Full desktop softphone for Intel Macs. Open the .dmg and drag Stratus to Applications. First launch: right-click the app and choose Open."
            url={macIntelUrl}
            buttonLabel="Download for Mac"
          />
        </div>
      )}

      {/* Tools — not a download; a migration utility that lives on its own. */}
      <div className="pt-2">
        <h2 className="text-sm font-semibold text-textSecondary uppercase tracking-wide mb-2">Tools</h2>
        <Link to="/import" className="card p-4 flex items-center gap-3 hover:bg-elevated transition-colors">
          <div className="w-11 h-11 rounded-xl bg-elevated flex items-center justify-center text-primary shrink-0">
            <Cable size={20} />
          </div>
          <div className="flex-1">
            <div className="font-semibold">Import from 3CX</div>
            <div className="text-sm text-textSecondary">
              Upload a 3CX backup — migrate a whole department to Stratus: users, phones, ring groups, receptionists, voicemail + greetings.
            </div>
          </div>
          <ChevronRight size={18} className="text-textSecondary" />
        </Link>
      </div>

      {isAdmin && (
        <Link to="/apps/beta-testers" className="card p-4 flex items-center gap-3 hover:bg-elevated transition-colors">
          <div className="w-11 h-11 rounded-xl bg-elevated flex items-center justify-center text-primary shrink-0">
            <FlaskConical size={20} />
          </div>
          <div className="flex-1">
            <div className="font-semibold">Ad Hoc Beta — Testers</div>
            <div className="text-sm text-textSecondary">Monitor who's enrolled in the iOS beta, device-slot usage, and build status.</div>
          </div>
          <ChevronRight size={18} className="text-textSecondary" />
        </Link>
      )}
    </div>
  );
}

function AppCard({ icon, title, subtitle, desc, url, buttonLabel, qr, external }: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  desc: string;
  url: string;
  buttonLabel: string;
  qr?: string | null;
  external?: boolean;
}) {
  return (
    <div className="card p-6 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-elevated flex items-center justify-center text-primary shrink-0">
          {icon}
        </div>
        <div>
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-textSecondary uppercase tracking-wide">{subtitle}</div>
        </div>
      </div>

      <p className="text-sm text-textSecondary flex-1">{desc}</p>

      {qr && (
        <img
          src={qr}
          alt={`${title} download QR code`}
          className="w-32 h-32 self-center bg-white p-2 rounded-lg"
        />
      )}

      <a
        href={url}
        target={external ? '_blank' : undefined}
        rel="noreferrer"
        download={external ? undefined : ''}
        className="btn-primary inline-flex items-center justify-center gap-2"
      >
        <Download size={15} /> {buttonLabel}
      </a>
    </div>
  );
}
